import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";

/**
 * Printout page-setup controls shared by the download dialog's two settings panels (the configurable
 * day view and the CDPH 530 form), so the two read as one screen rather than two hand-rolled ones.
 *
 * Domain-specific on purpose — this is Excel's page setup, not a general-purpose control. The generic
 * disclosure these panels also share lives in `UIToolkit/Panels/CollapsibleSection`.
 */

export type FitToPageSettingsProps = {
	fitToPage: boolean;
	fitToWidth: number;
	fitToHeight: number;
	onFitToPageChange: (value: boolean) => void;
	onFitToWidthChange: (value: number) => void;
	onFitToHeightChange: (value: number) => void;
	/**
	 * Whether to offer the "N pages wide by M tall" fields alongside the switch.
	 *
	 * `false` for a printout whose page budget is not the user's to pick — the CDPH 530 is one page
	 * wide by construction and pins both axes to 1, so the fields would be controls that change
	 * nothing. The switch still means what it says there: on, fit it to the page. Required rather than
	 * defaulted, so a new caller has to say which of the two it is.
	 */
	showPageCounts: boolean;
};

/**
 * Excel's own "Fit to N pages wide by M tall" page setup. The width/height fields stay mounted but
 * disabled when scaling is off, so their values survive toggling it back on.
 */
export const FitToPageSettings: FC<FitToPageSettingsProps> = ({
	fitToPage,
	fitToWidth,
	fitToHeight,
	onFitToPageChange,
	onFitToWidthChange,
	onFitToHeightChange,
	showPageCounts,
}) => {
	const { t } = useTranslation();

	return (
		<>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<SystemSwitch
					checked={fitToPage}
					onChange={() => onFitToPageChange(!fitToPage)}
					label={t("printout.fit.to.page")}
					dataTestId="printout-fit-to-page-switch"
				/>
				<InHouseTooltip title={t("printout.fit.to.page.tooltip")}>
					<InfoOutlined sx={{ fontSize: "18px" }} />
				</InHouseTooltip>
			</Box>
			{showPageCounts ? (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pl: 2, opacity: fitToPage ? 1 : 0.4 }}>
						<Typography variant="caption" sx={{ color: "primary.dark", fontWeight: 500 }}>
							{t("printout.fit.to.width")}
						</Typography>
						<SystemValidationTextField
							value={String(fitToWidth)}
							disabled={!fitToPage}
							onChange={(e) => onFitToWidthChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: 90 }}
							dataTestId="printout-fit-to-width"
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 1,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pl: 2, opacity: fitToPage ? 1 : 0.4 }}>
						<Typography variant="caption" sx={{ color: "primary.dark", fontWeight: 500 }}>
							{t("printout.fit.to.height")}
						</Typography>
						<SystemValidationTextField
							value={String(fitToHeight)}
							disabled={!fitToPage}
							onChange={(e) => onFitToHeightChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: 90 }}
							dataTestId="printout-fit-to-height"
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 0,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
					</Box>
				</Box>
			) : null}
		</>
	);
};
